import { Component, OnInit } from '@angular/core';
import {PhysicalAsset} from "../../../models/physical-asset";
import {Equipment} from "../../../models/equipment";
import {ActivatedRoute, Router} from "@angular/router";
import {PhysicalAssetManagerService} from "../../../services/physical-asset-manager.service";
import {FieldServiceManagerService} from "../../../services/field-service-manager.service";
import {FieldService} from "../../../models/field-service";
import {EquipmentManagerService} from "../../../services/equipment-manager.service";
import {UiManagerService} from "../../../services/ui-manager.service";
import {Paginator} from "../../../models/paginator";
import {DetailCardContent} from "../../../components/detail-card/detail-card.component";
import {MaintenanceManagerService} from "../../../services/maintenance-manager.service";
import {TableAction, TableColumn, TableColumnType} from "../../../components/table/table.component";
import {Maintenance, MaintenanceStatusUtils} from "../../../models/maintenance";
import {environment} from "@environments/environment";
import {TranslatePipe} from "@ngx-translate/core";

enum CreationMode {
  NONE, NEW, UPDATE
}

@Component({
  selector: 'app-physical-asset-page',
  templateUrl: './physical-asset-page.component.html',
  styleUrls: ['./physical-asset-page.component.scss']
})
export class PhysicalAssetPageComponent implements OnInit {

  isLoading = true;

  mode: CreationMode = CreationMode.NONE;

  equipment: Equipment;
  physicalAsset: PhysicalAsset;
  fieldServices: FieldService[];
  setupTempDate = new Date();

  maintenances: Maintenance[];
  maintenancesPaginator: Paginator<Maintenance>;

  constructor(
    private equipmentManagerService: EquipmentManagerService,
    private physicalAssetManagerService: PhysicalAssetManagerService,
    private maintenanceManagerService: MaintenanceManagerService,
    private fieldServiceManagerService: FieldServiceManagerService,
    private uiManagerService: UiManagerService,
    public route: ActivatedRoute,
    public router: Router,
    public translatePipe: TranslatePipe
  ) { }

  ngOnInit(): void {

    // Initialize new equipment
    this.physicalAsset = new PhysicalAsset();

    this.route.paramMap.subscribe(async params => {

      const assetId = params.get('assetId');

      this.fieldServices = await this.fieldServiceManagerService.getFieldServices(null, new Paginator<FieldService>({ignorePagination: true}));

      if(assetId) {

        this.physicalAsset = await this.physicalAssetManagerService.getPhysicalAsset(assetId);
        this.setupTempDate = new Date(this.physicalAsset.deployedAt);

        if (this.physicalAsset.fieldService) {
          this.physicalAsset.fieldServiceId = this.physicalAsset.fieldService.id;
        }

        // Get all maintenances for this physical asset
        this.maintenancesPaginator = new Paginator<Maintenance>({orderBy: 'id', sorting: "DESC"});
        this.maintenancesPaginator.onPageResults = (maintenances: Maintenance[]) => {
          this.maintenances = maintenances;
        }
        await this.maintenanceManagerService.getMaintenancesForPhysicalAsset(this.physicalAsset, null, this.maintenancesPaginator);

        this.mode = CreationMode.UPDATE;

      } else {

        this.mode = CreationMode.NEW;

        this.equipment = await this.equipmentManagerService.getEquipment(params.get('id'));
        this.physicalAsset.deployedAt = new Date().getTime();
        this.maintenances = [];

      }

      this.physicalAsset.modelId = this.equipment ? this.equipment.id : this.physicalAsset.model.id;

      this.isLoading = false;

    });

  }

  getCardTitle() {
    switch (this.mode) {
      case CreationMode.NONE:
        return '&nbsp;';
      case CreationMode.NEW:
        return this.translatePipe.transform('Create asset');
      case CreationMode.UPDATE:
        return `${this.translatePipe.transform('Manage asset')} ${this.physicalAsset.serialNumber}`;
    }
  }

  async save() {

    this.isLoading = true;

    switch (this.mode) {

      case CreationMode.NEW:
        this.physicalAsset = await this.physicalAssetManagerService.createPhysicalAsset(this.physicalAsset);
        await this.router.navigate([this.physicalAsset.id], { relativeTo: this.route, replaceUrl: true });
        break;

      case CreationMode.UPDATE:
        await this.physicalAssetManagerService.updatePhysicalAsset(this.physicalAsset.id, this.physicalAsset);
        await this.router.navigate(['../..'], { relativeTo: this.route });
        break;
    }

    this.isLoading = false;

  }

  async delete() {
    await this.uiManagerService.showConfirmDialog('Remove asset', 'Are you sure do you want to remove this asset?<br>This operation is irreversible.',
      async () => {
        await this.physicalAssetManagerService.removePhysicalAsset(this.physicalAsset.id);
        await this.router.navigate(['../..'], { relativeTo: this.route });
      });
  }

  filterMaintenances(filter: string) {
    this.uiManagerService.notify(this.translatePipe.transform('Not implemented'));
  }

  getMaintenancesTableColumns(): TableColumn[] {
    return [
      { key: 'id', type: TableColumnType.DEFAULT, title: 'ID', isMasterKey: true },
      { key: 'field_operator', type: TableColumnType.DEFAULT, title: 'Field Operator' },
      { key: 'expert', type: TableColumnType.DEFAULT, title: 'Remote Expert' },
      { key: 'status', type: TableColumnType.DEFAULT, title: 'Status' },
      { key: 'created_at', type: TableColumnType.DATE, title: 'Creation date' },
      { key: 'closed_at', type: TableColumnType.DATE, title: 'Closed date' },
      { key: 'scheduled_at', type: TableColumnType.DATE, title: 'Schedule date' },
      { key: 'scheduled', type: TableColumnType.BOOLEAN, title: 'Is scheduled' },
      { key: 'is_support_required', type: TableColumnType.BOOLEAN, title: 'Is expert required' },
      { key: 'is_bot', type: TableColumnType.BOOLEAN, title: 'Chatbot only' },
    ];
  }

  getMaintenancesTableData() {
    return this.maintenances.map(maintenance => {
      return {
        id: maintenance.id,
        field_operator: maintenance.fieldWorker?.name || '',
        expert: maintenance.remoteExpert?.name || '',
        status: this.translatePipe.transform(MaintenanceStatusUtils.toString(maintenance.status)),
        created_at: new Date(maintenance.createdAt),
        closed_at: new Date(maintenance.closedAt),
        scheduled_at: maintenance.isExtraordinary ? '-' : new Date(maintenance.scheduledAt),
        scheduled: !maintenance.isExtraordinary,
        is_support_required: maintenance.isSupportRequired,
        is_bot: maintenance.isBotEnabled,
      };
    });
  }

  showMaintenance(maintenanceId: string) {
    this.router.navigate(['maintenance', maintenanceId]);
  }

  getModes() {
    return CreationMode;
  }

  get setupDate(): Date {
    return this.setupTempDate;
  }

  set setupDate(date: Date) {
    this.physicalAsset.deployedAt = date.getTime();
    this.setupTempDate = date;
  }

  get DetailCardContent() { return DetailCardContent; }

  get environment() { return environment; }

}
